<script setup lang="ts">
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import { logApiError } from '~/utils/logger'

const authStore = useAuthStore()
const userData = computed(() => authStore.userData)
const avatarLoadError = ref(false)

// Reset error state when avatar URL changes
watch(() => userData.value?.avatar, () => {
  avatarLoadError.value = false
})

async function logout() {
  try {
    // Sign out using auth store
    await authStore.logout()

    await navigateTo({ name: 'auth-login' }, { replace: true })
  }
  catch (error) {
    logApiError('/auth/logout', error)

    // Force navigation to login even on error
    await navigateTo({ name: 'auth-login' }, { replace: true })
  }
}

const userProfileList = computed(() => {
  return [
    { type: 'divider' },
    {
      type: 'navItem',
      icon: 'bx-user',
      title: 'Profile',
      to: { name: 'user-profiel' },
    },
    {
      type: 'navItem',
      icon: 'bx-cog',
      title: 'Settings',
      to: { name: 'user-profiel' },
    },
    {
      type: 'navItem',
      icon: 'bx-credit-card',
      title: 'Mijn kampen',
      to: {
        name: 'user-profiel',
      },
      badgeProps: { color: 'error', content: '4' },
    },
    { type: 'divider' },
  ]
})
</script>

<template>
  <VBadge
    v-if="userData"
    dot
    bordered
    location="bottom right"
    offset-x="1"
    offset-y="2"
    color="success"
  >
    <VAvatar
      size="38"
      class="cursor-pointer"
      :color="!(userData && userData.avatar && !avatarLoadError) ? 'primary' : undefined"
      :variant="!(userData && userData.avatar && !avatarLoadError) ? 'tonal' : undefined"
    >
      <VImg
        v-if="userData && userData.avatar && !avatarLoadError"
        :src="userData.avatar"
        @error="avatarLoadError = true"
      />
      <VIcon
        v-else
        icon="bx-user"
      />

      <!-- SECTION Menu -->
      <VMenu
        activator="parent"
        width="240"
        location="bottom end"
        offset="20px"
      >
        <VList>
          <VListItem>
            <div class="d-flex gap-2 align-center">
              <VListItemAction>
                <VBadge
                  dot
                  location="bottom right"
                  offset-x="3"
                  offset-y="3"
                  color="success"
                  bordered
                >
                  <VAvatar
                    :color="!(userData && userData.avatar && !avatarLoadError) ? 'primary' : undefined
                    "
                    :variant="!(userData && userData.avatar && !avatarLoadError) ? 'tonal' : undefined
                    "
                  >
                    <VImg
                      v-if="userData && userData.avatar && !avatarLoadError"
                      :src="userData.avatar"
                      @error="avatarLoadError = true"
                    />
                    <VIcon
                      v-else
                      icon="bx-user"
                    />
                  </VAvatar>
                </VBadge>
              </VListItemAction>
              <div>
                <VListItemTitle class="font-weight-medium">
                  {{ userData.fullName || userData.username }}
                </VListItemTitle>
                <VListItemSubtitle class="text-disabled text-capitalize">
                  {{ userData.role }}
                </VListItemSubtitle>
              </div>
            </div>
          </VListItem>

          <PerfectScrollbar :options="{ wheelPropagation: false }">
            <template
              v-for="item in userProfileList"
              :key="item.title"
            >
              <VListItem
                v-if="item.type === 'navItem' && item.to"
                :to="item.to"
                :value="item.title"
              >
                <template #prepend>
                  <VIcon
                    :icon="item.icon"
                    size="22"
                  />
                </template>

                <VListItemTitle>{{ item.title }}</VListItemTitle>

                <template
                  v-if="item.badgeProps"
                  #append
                >
                  <VBadge
                    rounded
                    class="me-3"
                    v-bind="item.badgeProps"
                  />
                </template>
              </VListItem>

              <VDivider
                v-else-if="item.type === 'divider'"
                class="my-1"
              />
            </template>
            <VDivider class="my-1" />
            <VListItem
              prepend-icon="bx-power-off"
              @click="logout"
            >
              Logout
            </VListItem>
          </PerfectScrollbar>
        </VList>
      </VMenu>
      <!-- !SECTION -->
    </VAvatar>
  </VBadge>
</template>
